import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { SiteFooter } from "@/components/SiteFooter";
import PageMeta from "@/components/PageMeta";
import founderPhoto from "@/assets/founder-profile-circle.png.asset.json";

interface FounderPageRow {
  bio: string;
  disclosure: string;
  affiliate: string;
  updated_at: string;
}

const FALLBACK: FounderPageRow = {
  bio: "",
  disclosure: "",
  affiliate: "",
  updated_at: new Date().toISOString(),
};

const Founder = () => {
  const [data, setData] = useState<FounderPageRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: row, error } = await supabase
        .from("founder_page" as any)
        .select("bio, disclosure, affiliate, updated_at")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;
      if (error || !row) {
        setData(FALLBACK);
      } else {
        setData(row as unknown as FounderPageRow);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const formattedDate = data
    ? new Date(data.updated_at).toLocaleDateString("en-AU", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <PageMeta
        title="Founder & Trust — Sean Grobbelaar, GoToCoins"
        description="Meet the founder behind GoToCoins: background, methodology, disclosures and affiliate transparency for Australia's crypto research platform."
        path="/founder"
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Sean Grobbelaar",
          url: "https://gotocoin.au/founder",
          jobTitle: "Founder",
          worksFor: { "@type": "Organization", name: "GoToCoins", url: "https://gotocoin.au/" },
          sameAs: ["https://x.com/Sydneygotoman"],
        }}
      />
      {/* Header */}
      <header className="border-b border-border/60 bg-card/40 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> GoToCoins
          </Link>
          <div className="ml-auto text-sm text-muted-foreground">Founder & Trust</div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-10 max-w-3xl">
        {/* Avatar + intro */}
        <div className="flex flex-col items-center text-center">
          <img
            src={founderPhoto.url}
            alt="Sydney Gotoman, founder of GoToCoin"
            width={160}
            height={160}
            className="w-40 h-40 rounded-full shadow-elevation"
          />
          <h1 className="mt-6 text-3xl font-bold tracking-tight">Meet the founder</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The person behind GoToCoin, and why this site exists.
          </p>
        </div>

        {/* Bio */}
        <section className="mt-10">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-10/12" />
            </div>
          ) : (
            <div className="prose prose-invert max-w-none space-y-4">
              {data!.bio.split(/\n\n+/).map((para, i) => (
                <p key={i} className="text-base leading-relaxed text-foreground/90">
                  {para}
                </p>
              ))}
            </div>
          )}
        </section>

        {/* Disclosure + Affiliate callout */}
        <section className="mt-10 space-y-4">
          <Card className="border-warning/40 bg-warning/5">
            <CardContent className="py-5 space-y-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-warning mb-2">
                    Disclosure
                  </h2>
                  {loading ? (
                    <Skeleton className="h-16 w-full" />
                  ) : (
                    <p className="text-sm leading-relaxed text-foreground/90">
                      {data!.disclosure}
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t border-warning/20 pt-4 flex items-start gap-3">
                <Link2 className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-warning mb-2">
                    Affiliate relationships
                  </h3>
                  {loading ? (
                    <Skeleton className="h-12 w-full" />
                  ) : (
                    <p className="text-sm leading-relaxed text-foreground/90">
                      {data!.affiliate}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground text-right">
            Last updated:{" "}
            {loading ? <span className="inline-block w-24 h-3 bg-muted rounded animate-pulse" /> : formattedDate}
          </p>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Founder;
